import type { User } from "@assetwatch/auth";
import {
  canAccessModule,
  canPerformAction,
  type WorkAccessProfile,
  type WorkModule,
  WorkRole,
  type WorkRoleCode,
} from "@assetwatch/work-authorization";

export type {
  WorkAccessProfile,
  WorkCapability,
  WorkModule,
  WorkRoleCode,
} from "@assetwatch/work-authorization";

export { canAccessModule, canPerformAction };

export function canAccessWork(user: User | null): boolean {
  return Boolean(user);
}

export function moduleForPath(pathname: string): WorkModule | undefined {
  if (pathname === "/") return "HOME";
  if (pathname === "/requests" || pathname.startsWith("/requests/")) {
    return "REQUESTS";
  }
  if (pathname === "/work-orders" || pathname.startsWith("/work-orders/")) {
    return "WORK_ORDERS";
  }
  if (pathname === "/assets" || pathname.startsWith("/assets/")) {
    return "ASSETS";
  }
  if (pathname === "/team-users" || pathname.startsWith("/team-users/")) {
    return "TEAMS_USERS";
  }
  if (pathname === "/documents" || pathname.startsWith("/documents/")) {
    return "DOCUMENTS";
  }
  if (pathname === "/procedures" || pathname.startsWith("/procedures/")) {
    return "PROCEDURES";
  }
  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    return "SETTINGS";
  }
  return undefined;
}

export function fallbackPathForRole(profile: WorkAccessProfile) {
  if (canAccessModule(profile, "WORK_ORDERS")) return "/work-orders";
  return "/requests";
}

/** Canonical landing destination for each Work role. */
export function defaultPathForRole(profile: WorkAccessProfile) {
  switch (profile.role) {
    case "REQUESTER":
    case "OPERATOR":
      return "/requests";
    case "TECHNICIAN":
    case "ADMINISTRATOR":
      return "/work-orders";
  }
}

/** Web MVP navigation contract selected from the current Experience & Navigation page. */
export function canAccessWebNavigationModule(
  profile: WorkAccessProfile,
  module: WorkModule,
) {
  if (profile.role === WorkRole.OPERATOR)
    return canAccessModule(profile, module);

  const allowedModules: Readonly<
    Record<Exclude<WorkRoleCode, "OPERATOR">, readonly WorkModule[]>
  > = {
    REQUESTER: ["REQUESTS"],
    TECHNICIAN: ["WORK_ORDERS", "ASSETS", "PROCEDURES"],
    ADMINISTRATOR: [
      "WORK_ORDERS",
      "REQUESTS",
      "ASSETS",
      "PROCEDURES",
      "TEAMS_USERS",
      "SETTINGS",
    ],
  };

  return allowedModules[profile.role].includes(module);
}

export type WebNavigationModule = Extract<
  WorkModule,
  | "WORK_ORDERS"
  | "REQUESTS"
  | "ASSETS"
  | "PROCEDURES"
  | "TEAMS_USERS"
  | "SETTINGS"
>;

/**
 * Ordered primary Web destinations shown in the side navigation. This only
 * controls what is listed; route authorization stays with
 * canAccessWebNavigationModule. Operator is treated as Requester for now.
 */
export function webNavigationModulesForRole(
  role: WorkRoleCode,
): readonly WebNavigationModule[] {
  switch (role) {
    case "REQUESTER":
    case "OPERATOR":
      return ["REQUESTS"];
    case "TECHNICIAN":
      return ["WORK_ORDERS", "ASSETS", "PROCEDURES"];
    case "ADMINISTRATOR":
      return [
        "WORK_ORDERS",
        "REQUESTS",
        "ASSETS",
        "TEAMS_USERS",
        "PROCEDURES",
        "SETTINGS",
      ];
  }
}

export function redirectForRoleChange(
  pathname: string,
  previousRole: WorkRoleCode | undefined,
  profile: WorkAccessProfile,
) {
  const module = moduleForPath(pathname);
  if (module === "HOME") return fallbackPathForRole(profile);
  if (previousRole === profile.role) return null;
  if (!module || canAccessWebNavigationModule(profile, module)) return null;
  return fallbackPathForRole(profile);
}
